import { useState, useEffect, useCallback } from 'react'
import {
  Smartphone,
  Download,
  Shield,
  Zap,
  RefreshCw,
  ArrowLeft,
  Copy,
  ExternalLink,
  Link2,
  Info,
  Layers,
  Car,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { BRAND } from '../config/brand'

interface VersionInfo {
  version_name: string
  version_code: number
  filename: string
  download_url: string
  size_bytes: number
  built_at: string
  min_sdk: number
  package: string
  /** Populated by deploy/sync.sh from debug vs release APK path */
  apk_variant?: 'debug' | 'release' | 'unknown'
  /** dev = debug sideload; stable = release APK */
  channel?: 'dev' | 'stable' | 'unknown'
  channel_badge?: string
  channel_summary?: string
  features?: string[]
}

// Cheap shape check: reject entries the deploy script produced with empty or
// placeholder values (e.g. "${currentVersionCode}" literals that slipped in
// before the sidecar-based sync fix).
function isValidVersion(v: unknown): v is VersionInfo {
  if (!v || typeof v !== 'object') return false
  const r = v as Record<string, unknown>
  const codeOk = typeof r.version_code === 'number' && r.version_code > 0
  const nameOk =
    typeof r.version_name === 'string' &&
    r.version_name.length > 0 &&
    !r.version_name.includes('${') &&
    !r.version_name.startsWith('🔖')
  const urlOk = typeof r.download_url === 'string' && r.download_url.length > 0
  return codeOk && nameOk && urlOk
}

type FetchState = 'loading' | 'ok' | 'missing' | 'malformed'

const LATEST_APK_PATH = '/downloads/horton-latest.apk'
const VERSION_JSON_PATH = '/downloads/version.json'

function absoluteDownloadUrl(path: string): string {
  if (typeof window === 'undefined') return path
  const base = window.location.origin
  if (path.startsWith('http')) return path
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

function channelBadgeClass(channel?: string): string {
  if (channel === 'dev') {
    return 'border-amber-400/35 bg-amber-500/15 text-amber-50'
  }
  if (channel === 'stable') {
    return 'border-emerald-400/35 bg-emerald-500/15 text-emerald-50'
  }
  return 'border-white/20 bg-white/5 text-on-brand/85'
}

function fallbackChannelCopy(v: VersionInfo): { badge: string; summary: string; features: string[] } {
  const variant = v.apk_variant
  if (variant === 'debug') {
    return {
      badge: 'Development build',
      summary:
        'This manifest does not include channel metadata yet. The APK path indicates a debug build — use for engineering and Android Auto / device validation.',
      features: [
        'Typical debug build: Capacitor + horton.io, native plugins as enabled',
        'Prefer a fresh deploy with bash deploy/sync.sh android-apk for full labels',
      ],
    }
  }
  if (variant === 'release') {
    return {
      badge: 'Stable (release APK)',
      summary:
        'This manifest does not include channel metadata yet. The APK path indicates a release build — closer to a store-quality binary.',
      features: [
        'Typical release build: signed with the release keystore',
        'Redeploy android-apk to refresh version.json with full channel copy',
      ],
    }
  }
  return {
    badge: 'Published APK',
    summary:
      `Installable ${BRAND.assistant} Android package. See version and filename below; redeploy sync populates dev vs stable notes automatically.`,
    features: ['Capacitor web shell against horton.io', 'Sideload only — not from Google Play yet'],
  }
}

export function DownloadApp() {
  const [version, setVersion] = useState<VersionInfo | null>(null)
  const [state, setState] = useState<FetchState>('loading')
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const loadVersion = useCallback(async () => {
    setState('loading')
    try {
      const r = await fetch('/downloads/version.json', { cache: 'no-store' })
      if (!r.ok) {
        setVersion(null)
        setState('missing')
        return
      }
      const raw = (await r.json().catch(() => null)) as unknown
      if (!isValidVersion(raw)) {
        setVersion(null)
        setState('malformed')
        return
      }
      setVersion(raw)
      setState('ok')
    } catch {
      setVersion(null)
      setState('missing')
    }
  }, [])

  useEffect(() => {
    void loadVersion()
  }, [loadVersion])

  const formatSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(1)} MB`
  }

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const copyUrl = async (key: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedKey(key)
      window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000)
    } catch {
      /* ignore */
    }
  }

  const linkRows: { key: string; label: string; description?: string; path: string }[] = []
  if (state === 'ok' && version) {
    linkRows.push({
      key: 'versioned',
      label: 'This build (APK)',
      description: version.filename,
      path: version.download_url,
    })
  }
  linkRows.push(
    {
      key: 'latest',
      label: 'Latest APK (alias)',
      description: 'Always points at the most recently published build',
      path: LATEST_APK_PATH,
    },
    {
      key: 'manifest',
      label: 'Build manifest',
      description: 'JSON metadata (version, size, filename)',
      path: VERSION_JSON_PATH,
    },
  )

  const linksIntro =
    state === 'ok' && version ? (
      <>
        Copy or open any URL. The versioned file and{' '}
        <code className="text-on-brand/60">horton-latest.apk</code> both refer to{' '}
        <strong className="text-on-brand/80">this same published build</strong> (whatever was last
        uploaded by <code className="text-on-brand/60">deploy/sync.sh android-apk</code>). The
        channel badge below describes that build (dev vs release).
      </>
    ) : (
      <>
        Versioned APK appears after <code className="text-on-brand/60">version.json</code> loads.
        You can still use the paths below.
      </>
    )

  const buildStory =
    state === 'ok' && version
      ? (() => {
          const v = version
          const fb = fallbackChannelCopy(v)
          const channel =
            v.channel ??
            (v.apk_variant === 'debug'
              ? 'dev'
              : v.apk_variant === 'release'
                ? 'stable'
                : 'unknown')
          return {
            badge: v.channel_badge ?? fb.badge,
            summary: v.channel_summary ?? fb.summary,
            features: v.features && v.features.length > 0 ? v.features : fb.features,
            channel,
          }
        })()
      : null

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-brand-dark via-brand-dark-mid to-brand-dark text-on-brand pb-16 sm:pb-20 [color-scheme:dark]">
      <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-on-brand/60 hover:text-on-brand/90 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {BRAND.assistant}
        </Link>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-surface/10 backdrop-blur mb-6">
            <Smartphone className="h-10 w-10 text-brand-subtle" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">
            Get {BRAND.assistant} for Android
          </h1>
          <p className="text-lg text-on-brand/70 max-w-md mx-auto">
            Your personal growth companion, right in your pocket. Track goals, build habits, and stay on course.
          </p>
        </div>

        {/* Download card */}
        <div className="bg-surface/10 backdrop-blur-lg rounded-2xl border border-white/10 p-6 sm:p-8 mb-8">
          {state === 'loading' ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-on-brand/50" />
            </div>
          ) : state === 'ok' && version ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-xl font-semibold">{BRAND.assistant} Android App</h2>
                  <p className="text-sm text-on-brand/50 mt-1">
                    v{version.version_name} &middot; build {version.version_code} &middot;{' '}
                    {formatSize(version.size_bytes)} &middot; Built {formatDate(version.built_at)}
                  </p>
                  {version.apk_variant && version.apk_variant !== 'unknown' ? (
                    <p className="text-xs text-on-brand/40 mt-1">
                      Gradle variant:{' '}
                      <span className="font-mono text-on-brand/55">{version.apk_variant}</span>
                    </p>
                  ) : null}
                </div>
                {buildStory ? (
                  <span
                    className={`shrink-0 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${channelBadgeClass(buildStory.channel)}`}
                  >
                    {buildStory.badge}
                  </span>
                ) : null}
              </div>

              {buildStory ? (
                <div className="mb-6 rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-brand-subtle shrink-0 mt-0.5" aria-hidden />
                    <p className="text-sm text-on-brand/80 leading-relaxed">{buildStory.summary}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-on-brand/60 mb-2 flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5" aria-hidden />
                      What this build is for
                    </p>
                    <ul className="list-disc list-inside space-y-1.5 text-sm text-on-brand/70">
                      {buildStory.features.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}

              <a
                href={version.download_url}
                className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-xl bg-brand-primary hover:bg-brand-hover text-on-brand font-semibold text-lg transition-all shadow-lg shadow-brand-primary/25 hover:shadow-brand-primary/40"
              >
                <Download className="h-5 w-5" />
                Download APK
              </a>

              <p className="text-xs text-on-brand/40 text-center mt-3">
                Package: {version.package}
              </p>
            </>
          ) : (
            <div className="text-center py-8 space-y-3">
              <p className="text-on-brand/70">
                {state === 'malformed'
                  ? 'The latest build manifest looks corrupted.'
                  : 'No build available yet.'}
              </p>
              <p className="text-on-brand/40 text-xs">
                {state === 'malformed'
                  ? 'Re-run `bash deploy/sync.sh android-apk` after building a fresh APK. The sidecar horton-build.json next to the APK is the source of truth.'
                  : 'Build and publish one with: cd ui-frontend && npm run mobile:android:apk && bash ../deploy/sync.sh android-apk'}
              </p>
              <button
                onClick={() => void loadVersion()}
                className="inline-flex items-center gap-1.5 text-sm text-brand-subtle hover:text-on-brand transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Check again
              </button>
            </div>
          )}

          {state !== 'loading' && (
            <div
              className={`border-t border-white/10 pt-8 mt-8 ${state === 'ok' && version ? '' : 'text-left'}`}
            >
              <h3 className="text-sm font-semibold text-on-brand/90 mb-1 flex items-center gap-2">
                <Link2 className="h-4 w-4 text-brand-subtle" />
                All download links
              </h3>
              <p className="text-xs text-on-brand/45 mb-4">{linksIntro}</p>
              <ul className="space-y-3">
                {linkRows.map((row) => {
                  const href = row.path
                  const full = absoluteDownloadUrl(href)
                  return (
                    <li
                      key={row.key}
                      className="rounded-xl bg-black/20 border border-white/10 p-3 sm:p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-on-brand">{row.label}</p>
                          {row.description ? (
                            <p className="text-xs text-on-brand/45 mt-0.5">{row.description}</p>
                          ) : null}
                          <p className="text-xs font-mono text-brand-subtle/90 break-all mt-2 select-all">
                            {full}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => void copyUrl(row.key, full)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-medium text-on-brand/90 transition-colors"
                            aria-label={`Copy ${row.label}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                            {copiedKey === row.key ? 'Copied' : 'Copy'}
                          </button>
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-primary/30 hover:bg-brand-primary/45 text-xs font-medium text-on-brand transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open
                          </a>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Install instructions */}
        <div className="bg-surface/5 rounded-2xl border border-white/10 p-6 sm:p-8 mb-8">
          <h3 className="text-lg font-semibold mb-4">How to install</h3>
          <ol className="space-y-3 text-on-brand/70 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary/20 text-brand-subtle text-xs font-bold flex items-center justify-center">
                1
              </span>
              <span>Tap <strong className="text-on-brand">Download APK</strong> above on your Android phone</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary/20 text-brand-subtle text-xs font-bold flex items-center justify-center">
                2
              </span>
              <span>
                If prompted, go to <strong className="text-on-brand">Settings &rarr; Install unknown apps</strong> and allow your browser
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary/20 text-brand-subtle text-xs font-bold flex items-center justify-center">
                3
              </span>
              <span>Open the downloaded file and tap <strong className="text-on-brand">Install</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary/20 text-brand-subtle text-xs font-bold flex items-center justify-center">
                4
              </span>
              <span>Open {BRAND.assistant} and log in with your existing account</span>
            </li>
          </ol>
        </div>

        {/* Android Auto: sideload is hidden from the car until Unknown sources is on */}
        <div className="bg-amber-500/10 rounded-2xl border border-amber-400/25 p-6 sm:p-8 mb-8">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-amber-50">
            <Car className="h-5 w-5 text-amber-200 shrink-0" aria-hidden />
            Android Auto (Uconnect, factory radios)
          </h3>
          <p className="text-sm text-on-brand/75 mb-4 leading-relaxed">
            {BRAND.assistant} is installed from this page, not from Google Play. The app must declare an Android Auto
            category that <strong className="text-amber-100/95">phone projection (Gearhead)</strong> actually
            lists — IoT-only car apps often target in-dash Android Automotive OS and never show under{' '}
            <strong className="text-amber-100/95">Customize launcher</strong> on the phone. Use a current {BRAND.assistant}
            APK (POI + theme metadata) and reinstall if you still don&apos;t see it.
          </p>
          <p className="text-sm text-on-brand/75 mb-4 leading-relaxed">
            <strong className="text-amber-100/95">Android Auto also hides sideloaded apps</strong> from the car
            until you allow unknown sources in Android Auto developer settings (below).
          </p>
          <p className="text-xs font-semibold text-amber-100/90 mb-2">Turn on launcher access for sideloaded car apps</p>
          <ol className="space-y-2.5 text-on-brand/75 text-sm list-decimal list-inside">
            <li>
              On the phone, open the <strong className="text-on-brand">Android Auto</strong> app (by Google).
            </li>
            <li>
              Open <strong className="text-on-brand">Settings</strong>, scroll to the bottom, and tap{' '}
              <strong className="text-on-brand">Version</strong> / <strong className="text-on-brand">About</strong>{' '}
              repeatedly (about ten times) until you see a message that{' '}
              <strong className="text-on-brand">developer mode</strong> is enabled. Menu labels vary slightly by
              Android Auto version.
            </li>
            <li>
              Open the <strong className="text-on-brand">⋮</strong> overflow menu →{' '}
              <strong className="text-on-brand">Developer settings</strong> and enable{' '}
              <strong className="text-on-brand">Unknown sources</strong> (sometimes worded as allowing apps from
              unknown sources in the car launcher).
            </li>
            <li>
              Fully quit Android Auto, unplug USB, plug in again, and start Android Auto on the car. Use a{' '}
              <strong className="text-on-brand">data-capable</strong> cable; charge-only cables never show phone apps.
            </li>
          </ol>
          <p className="text-xs text-on-brand/45 mt-4 leading-relaxed">
            Long term, listing on{' '}
            <a
              href="https://developer.android.com/training/cars/distribute"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/90 underline hover:text-amber-100"
            >
              Google Play with the cars form factor
            </a>{' '}
            is how most production apps appear for all drivers without this step. Official template overview:{' '}
            <a
              href="https://developer.android.com/training/cars/apps/auto"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-200/90 underline hover:text-amber-100"
            >
              Android Auto templated apps
            </a>
            .
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Zap, title: 'Fast', desc: 'Native shell with instant load times' },
            { icon: Shield, title: 'Secure', desc: 'Same encryption as the web app' },
            { icon: RefreshCw, title: 'Always Current', desc: 'UI updates automatically from the server' },
          ].map((f) => (
            <div
              key={f.title}
              className="bg-surface/5 rounded-xl border border-white/10 p-4 text-center"
            >
              <f.icon className="h-6 w-6 mx-auto mb-2 text-brand-subtle" />
              <h4 className="font-medium text-sm">{f.title}</h4>
              <p className="text-xs text-on-brand/50 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-on-brand/30 mt-12">
          Requires Android 5.1 or later. Not on Google Play yet.
        </p>
      </div>
    </div>
  )
}
